/**
 * Схеми сутностей «Хроніки Альбіону» — єдине джерело для:
 *  • форми створення (структуровані поля по типах),
 *  • рендеру стат-картки (StatCard читає ті самі ключі з frontmatter).
 * Базується на Design Brief §11.
 */

export type FieldKind = 'text' | 'textarea' | 'int' | 'signed' | 'enum' | 'tags' | 'pips';
export type FieldGroup = 'stat' | 'social' | 'meta';

export interface Field {
  key: string;
  label: string;
  kind: FieldKind;
  group?: FieldGroup;      // stat → стат-стрічка/піпи; social → соц-блок; meta → дрібне
  options?: string[];      // для enum
  max?: number;            // для pips
  gm?: boolean;            // лише в режимі Майстра
  placeholder?: string;
}

export interface EntitySchema {
  type: string;
  label: string;           // людська назва типу (для пікера/бейджа)
  icon: string;            // ключ dvIcons
  cat: string;             // CSS-токен кольору категорії, напр. 'cat-city'
  treePath: string;        // куди в дерево
  category: string;        // значення поля category
  native?: boolean;        // показувати поле «оригінальна назва»
  abilities?: boolean;     // має блок типованих вмінь (Пасивна/Дія/Реакція)
  combat?: boolean;        // має бойовий блок (складність/пороги/HP/стрес/атака)
  fields: Field[];
  sections: string[];      // секції опису (прози)
  gmSections?: string[];   // секції опису лише для Майстра
}

/** Спільний бойовий блок DH — додається до Ворога/NPC/Союзника. */
export const COMBAT_FIELDS: Field[] = [
  { key: 'difficulty', label: 'Складність', kind: 'int', group: 'stat' },
  { key: 'thrMajor', label: 'Поріг Major', kind: 'int', group: 'stat' },
  { key: 'thrSevere', label: 'Поріг Severe', kind: 'int', group: 'stat' },
  { key: 'atk', label: 'Модиф. атаки', kind: 'signed', group: 'stat' },
  { key: 'hp', label: 'HP', kind: 'pips', group: 'stat', max: 6 },
  { key: 'stress', label: 'Стрес', kind: 'pips', group: 'stat', max: 6 },
  { key: 'attack', label: 'Стандартна атака', kind: 'text', group: 'stat', placeholder: 'Удар · Впритул · 1d8+2 фіз' },
];

const HP = (max = 6): Field => ({ key: 'hp', label: 'HP', kind: 'pips', group: 'stat', max });
const STRESS = (max = 6): Field => ({ key: 'stress', label: 'Стрес', kind: 'pips', group: 'stat', max });

export const SCHEMAS: Record<string, EntitySchema> = {
  adversary: {
    type: 'adversary', label: 'Ворог', icon: 'beast', cat: 'cat-character', treePath: 'Characters/Bestiary', category: 'Бестіарій', native: true, abilities: true, combat: true,
    fields: [
      { key: 'tier', label: 'Рівень (Tier)', kind: 'enum', group: 'meta', options: ['1', '2', '3', '4'] },
      { key: 'advType', label: 'Тип ворога', kind: 'enum', group: 'meta', options: ['Solo', 'Bruiser', 'Horde', 'Leader', 'Minion', 'Ranged', 'Skulk', 'Social', 'Standard', 'Support'] },
      { key: 'creature', label: 'Категорія істоти', kind: 'text', group: 'meta', placeholder: 'Звір, Дух, Нежить…' },
      { key: 'motives', label: 'Мотиви й тактика', kind: 'textarea', group: 'meta', placeholder: 'захищати лігво, відкидати, переслідувати' },
      { key: 'difficulty', label: 'Складність', kind: 'int', group: 'stat' },
      { key: 'thrMajor', label: 'Поріг Major', kind: 'int', group: 'stat' },
      { key: 'thrSevere', label: 'Поріг Severe', kind: 'int', group: 'stat' },
      { key: 'atk', label: 'Модиф. атаки', kind: 'signed', group: 'stat' },
      HP(6), STRESS(3),
      { key: 'attack', label: 'Стандартна атака', kind: 'text', group: 'stat', placeholder: 'Удар лапою · Впритул · 1d8+2 фіз' },
      { key: 'experiences', label: 'Досвіди', kind: 'tags', group: 'stat', placeholder: 'Чуття землі +2' },
    ],
    sections: ['Опис', 'Поведінка / Екологія', 'Тактика', 'Де зустрічається', 'Лут'],
    gmSections: ['Нотатки Майстра'],
  },
  npc: {
    type: 'npc', label: 'NPC', icon: 'npc', cat: 'cat-character', treePath: 'Characters/NPCs', category: 'NPC', abilities: true, combat: true,
    fields: [
      { key: 'role', label: 'Роль', kind: 'enum', group: 'meta', options: ['Правитель', 'Торговець', 'Вчений', 'Вартовий', 'Жрець', 'Ремісник', 'Інше'] },
      { key: 'faction', label: 'Фракція', kind: 'text', group: 'meta' },
      { key: 'location', label: 'Локація', kind: 'text', group: 'meta' },
      { key: 'disposition', label: 'Ставлення', kind: 'enum', group: 'meta', options: ['Союзник', 'Нейтральний', 'Ворожий'] },
      { key: 'importance', label: 'Важливість', kind: 'enum', group: 'meta', options: ['Другорядний', 'Важливий', 'Ключовий'] },
      ...COMBAT_FIELDS,
      { key: 'motive', label: 'Мотив', kind: 'textarea', group: 'social' },
      { key: 'tactics', label: 'Тактика', kind: 'textarea', group: 'social' },
      { key: 'secret', label: 'Таємниця', kind: 'textarea', group: 'social', gm: true },
    ],
    sections: ['Огляд', 'Зовнішність', 'Манера / Голос', 'Історія', 'Звʼязки', 'Гачки для пригод'],
    gmSections: ['Секрети'],
  },
  character: {
    type: 'character', label: 'Герой гравця', icon: 'ally', cat: 'cat-character', treePath: 'Characters/PCs', category: 'Герої', abilities: true,
    fields: [
      { key: 'ancestry', label: 'Рід (Ancestry)', kind: 'text', group: 'meta' },
      { key: 'community', label: 'Спільнота', kind: 'text', group: 'meta' },
      { key: 'class', label: 'Клас', kind: 'text', group: 'meta' },
      { key: 'level', label: 'Рівень', kind: 'int', group: 'meta' },
      { key: 'evasion', label: 'Ухилення', kind: 'int', group: 'stat' },
      HP(6), STRESS(6), { key: 'hope', label: 'Надія', kind: 'pips', group: 'stat', max: 6 },
      { key: 'domains', label: 'Домени', kind: 'tags', group: 'stat' },
      { key: 'experiences', label: 'Досвіди', kind: 'tags', group: 'stat' },
    ],
    sections: ['Огляд', 'Зовнішність', 'Передісторія', 'Особистість', 'Звʼязки', 'Спорядження'],
  },
  city: {
    type: 'city', label: 'Місто / поселення', icon: 'city', cat: 'cat-city', treePath: 'World/Cities', category: 'Міста', native: true,
    fields: [
      { key: 'settlementType', label: 'Тип', kind: 'enum', group: 'meta', options: ['Столиця', 'Місто', 'Містечко', 'Село', 'Застава', 'Руїни'] },
      { key: 'region', label: 'Регіон', kind: 'text', group: 'meta' },
      { key: 'population', label: 'Населення', kind: 'text', group: 'stat' },
      { key: 'ruler', label: 'Правитель', kind: 'text', group: 'stat' },
      { key: 'rulingFaction', label: 'Влада', kind: 'text', group: 'stat' },
      { key: 'prosperity', label: 'Добробут', kind: 'enum', group: 'stat', options: ['Процвітає', 'Стабільне', 'Занепадає'] },
      { key: 'dangerLevel', label: 'Рівень загрози', kind: 'enum', group: 'stat', options: ['Низький', 'Помірний', 'Високий'] },
      { key: 'notableFor', label: 'Відоме за', kind: 'tags', group: 'stat' },
    ],
    sections: ['Огляд', 'Історія', 'Райони', 'Влада й закон', 'Економіка', 'Визначні місця', 'Чутки'],
    gmSections: ['Оборона / Загрози', 'Секрети'],
  },
  location: {
    type: 'location', label: 'Локація', icon: 'location', cat: 'cat-location', treePath: 'World/Locations', category: 'Локації',
    fields: [
      { key: 'locType', label: 'Тип', kind: 'enum', group: 'meta', options: ['Підземелля', 'Орієнтир', 'Дикі землі', 'Руїни', 'Святилище', 'Лігво'] },
      { key: 'region', label: 'Регіон', kind: 'text', group: 'meta' },
      { key: 'dangerTier', label: 'Рівень загрози', kind: 'enum', group: 'stat', options: ['1', '2', '3', '4'] },
      { key: 'environment', label: 'Середовище', kind: 'enum', group: 'stat', options: ['Ліс', 'Гори', 'Болото', 'Печери', 'Узбережжя', 'Місто'] },
      { key: 'difficulty', label: 'Складність', kind: 'int', group: 'stat' },
      { key: 'notableFeatures', label: 'Особливості', kind: 'tags', group: 'stat' },
    ],
    sections: ['Огляд', 'Атмосфера', 'Зони', 'Мешканці', 'Скарби'],
    gmSections: ['Небезпеки', 'Секрети'],
  },
  region: {
    type: 'region', label: 'Регіон', icon: 'region', cat: 'cat-world', treePath: 'World/Regions', category: 'Регіони',
    fields: [
      { key: 'climate', label: 'Клімат', kind: 'text', group: 'stat' },
      { key: 'terrain', label: 'Ландшафт', kind: 'tags', group: 'stat' },
      { key: 'peoples', label: 'Народи', kind: 'tags', group: 'stat' },
      { key: 'magicLevel', label: 'Рівень магії', kind: 'enum', group: 'stat', options: ['Низький', 'Помірний', 'Високий'] },
    ],
    sections: ['Огляд', 'Географія', 'Народи й культури', 'Політика', 'Історія', 'Локації'],
  },
  faction: {
    type: 'faction', label: 'Фракція', icon: 'faction', cat: 'cat-faction', treePath: 'Factions', category: 'Фракції',
    fields: [
      { key: 'factionType', label: 'Тип', kind: 'enum', group: 'meta', options: ['Гільдія', 'Культ', 'Орден', 'Двір', 'Клан', 'Компанія'] },
      { key: 'leader', label: 'Лідер', kind: 'text', group: 'stat' },
      { key: 'headquarters', label: 'Осідок', kind: 'text', group: 'stat' },
      { key: 'strength', label: 'Сила', kind: 'enum', group: 'stat', options: ['Слабка', 'Помірна', 'Велика'] },
      { key: 'reach', label: 'Вплив', kind: 'enum', group: 'stat', options: ['Локальний', 'Регіональний', 'Світовий'] },
    ],
    sections: ['Огляд', 'Цілі', 'Структура', 'Чільні постаті', 'Історія', 'Ресурси'],
    gmSections: ['Таємні плани'],
  },
  item: {
    type: 'item', label: 'Предмет / артефакт', icon: 'item', cat: 'cat-item', treePath: 'Items', category: 'Предмети', abilities: true,
    fields: [
      { key: 'itemType', label: 'Тип', kind: 'enum', group: 'meta', options: ['Зброя', 'Броня', 'Розхідник', 'Реліквія', 'Дивовижа', 'Інструмент'] },
      { key: 'rarity', label: 'Рідкість', kind: 'enum', group: 'meta', options: ['Звичайна', 'Незвичайна', 'Рідкісна', 'Легендарна'] },
      { key: 'tier', label: 'Рівень (Tier)', kind: 'enum', group: 'meta', options: ['1', '2', '3', '4'] },
      { key: 'damage', label: 'Шкода', kind: 'text', group: 'stat', placeholder: '1d8+2 фіз' },
      { key: 'range', label: 'Дальність', kind: 'text', group: 'stat' },
      { key: 'price', label: 'Ціна', kind: 'text', group: 'stat', placeholder: '3 мішки' },
      { key: 'feature', label: 'Особливість', kind: 'textarea', group: 'stat' },
    ],
    sections: ['Опис', 'Історія / Походження', 'Як використовувати', 'Де знайти'],
    gmSections: ['Нотатки Майстра'],
  },
  ability: {
    type: 'ability', label: 'Унікальне вміння', icon: 'ability', cat: 'cat-ability', treePath: 'Abilities', category: 'Вміння',
    fields: [
      { key: 'source', label: 'Джерело', kind: 'enum', group: 'meta', options: ['Клас', 'Домен', 'Рід', 'Предмет', 'Дар'] },
      { key: 'domain', label: 'Домен', kind: 'text', group: 'meta' },
      { key: 'featureType', label: 'Тип', kind: 'enum', group: 'meta', options: ['Пасивна', 'Дія', 'Реакція'] },
      { key: 'cost', label: 'Вартість', kind: 'text', group: 'stat', placeholder: '2 Надії' },
      { key: 'range', label: 'Дальність', kind: 'text', group: 'stat' },
      { key: 'effect', label: 'Ефект', kind: 'textarea', group: 'stat' },
    ],
    sections: ['Опис', 'Як працює', 'Взаємодії', 'Приклади'],
  },
  quest: {
    type: 'quest', label: 'Квест', icon: 'quest', cat: 'cat-quest', treePath: 'Quests', category: 'Квести',
    fields: [
      { key: 'status', label: 'Статус', kind: 'enum', group: 'meta', options: ['Чутка', 'Активний', 'Завершений', 'Провалений'] },
      { key: 'questType', label: 'Тип', kind: 'enum', group: 'meta', options: ['Основний', 'Побічний', 'Особистий', 'Фракційний'] },
      { key: 'giver', label: 'Видавець', kind: 'text', group: 'stat' },
      { key: 'location', label: 'Локація', kind: 'text', group: 'stat' },
      { key: 'reward', label: 'Нагорода', kind: 'text', group: 'stat' },
      { key: 'objectives', label: 'Завдання', kind: 'tags', group: 'stat' },
    ],
    sections: ['Завʼязка', 'Перебіг', 'Дійові особи', 'Ускладнення', 'Нагороди'],
    gmSections: ['Таємниці Майстра'],
  },
  campaign: {
    type: 'campaign', label: 'Кампанія', icon: 'campaign', cat: 'cat-campaign', treePath: 'Campaigns', category: 'Кампанії',
    fields: [
      { key: 'status', label: 'Статус', kind: 'enum', group: 'meta', options: ['Планується', 'Триває', 'Завершена'] },
      { key: 'tierRange', label: 'Рівні', kind: 'text', group: 'stat' },
      { key: 'sessionCount', label: 'Сесій', kind: 'int', group: 'stat' },
      { key: 'themes', label: 'Теми', kind: 'tags', group: 'stat' },
    ],
    sections: ['Огляд', 'Завʼязка', 'Арки', 'Дійові особи', 'Локації'],
    gmSections: ['Нотатки Майстра'],
  },
  lore: {
    type: 'lore', label: 'Лор / легенда', icon: 'lore', cat: 'cat-lore', treePath: 'Lore/Legends', category: 'Лор',
    fields: [
      { key: 'era', label: 'Доба', kind: 'text', group: 'meta' },
      { key: 'truth', label: 'Достовірність', kind: 'enum', group: 'meta', options: ['Підтверджено', 'Легенда', 'Єресь'] },
    ],
    sections: ['Оповідь', 'Деталі', 'Повʼязане'],
  },
  deity: {
    type: 'deity', label: 'Божество', icon: 'deity', cat: 'cat-deity', treePath: 'Lore/Deities', category: 'Міфи й боги',
    fields: [
      { key: 'portfolio', label: 'Сфера', kind: 'tags', group: 'meta' },
      { key: 'status', label: 'Стан', kind: 'enum', group: 'meta', options: ['Активне', 'Дрімає', 'Мертве', 'Увʼязнене'] },
    ],
    sections: ['Міф', 'Вчення', 'Культ', 'Прояви', 'Реліквії'],
    gmSections: ['Таємниці'],
  },
  culture: {
    type: 'culture', label: 'Народ / культура', icon: 'people', cat: 'cat-people', treePath: 'Peoples', category: 'Народи',
    fields: [
      { key: 'homeland', label: 'Батьківщина', kind: 'text', group: 'meta' },
      { key: 'language', label: 'Мова', kind: 'text', group: 'meta' },
      { key: 'values', label: 'Цінності', kind: 'tags', group: 'stat' },
    ],
    sections: ['Огляд', 'Спосіб життя', 'Вірування', 'Естетика', 'Видатні постаті'],
  },
  event: {
    type: 'event', label: 'Подія', icon: 'timeline', cat: 'cat-lore', treePath: 'Lore/Timeline', category: 'Хронологія',
    fields: [
      { key: 'date', label: 'Дата / доба', kind: 'text', group: 'meta' },
      { key: 'scope', label: 'Масштаб', kind: 'enum', group: 'meta', options: ['Локальна', 'Регіональна', 'Світова'] },
      { key: 'participants', label: 'Учасники', kind: 'tags', group: 'stat' },
    ],
    sections: ['Що сталося', 'Наслідки'],
  },
};

/** Порядок типів у пікері створення. */
export const CREATE_ORDER = [
  'city', 'location', 'region', 'npc', 'character', 'adversary',
  'faction', 'item', 'ability', 'quest', 'campaign', 'lore', 'deity', 'culture', 'event',
];

export function getSchema(type: string | undefined | null): EntitySchema | null {
  if (!type) return null;
  return SCHEMAS[type] ?? null;
}
